/* ============================================================================
 * hotspot-dots.js — INTERACTION ONLY.
 *
 * The dots themselves are now SERVER-RENDERED in Liquid (snippets/
 * hotspot-dots-render.liquid) as <button class="hotspot-dot" ...> inside each
 * product media wrapper, positioned with inline left/top %. Because they are part
 * of the section's HTML, they re-render automatically with the section on every
 * theme-editor edit — this file does NOT create, place, mount, observe, or retry
 * anything. It only shows a tooltip when a dot is tapped/hovered.
 *
 * All wiring is via event delegation on `document`, so it survives section
 * re-renders without re-binding: the listeners are attached once to a node that
 * never gets replaced, and they look up `.hotspot-dot` at event time.
 * ========================================================================== */
(function () {
  "use strict";

  var openDot = null;   // the dot whose tooltip is currently open
  var openTip = null;   // its tooltip element (appended to <body>)
  var rafId = null;

  // ---- Tooltip element (one reused node, appended to <body> to escape any
  //      overflow:hidden on the gallery). Built lazily on first use. ----------
  function ensureTip() {
    if (openTip && document.body.contains(openTip)) return openTip;
    var tip = document.createElement("div");
    tip.className = "hotspot-tooltip";
    tip.setAttribute("role", "tooltip");
    tip.innerHTML =
      '<span class="hotspot-tip-inner">' +
        '<img class="hotspot-tip-img" alt="" loading="lazy" hidden>' +
        '<span class="hotspot-tip-title"></span>' +
        '<span class="hotspot-tip-body"></span>' +
      '</span>';
    document.body.appendChild(tip);
    openTip = tip;
    return tip;
  }

  function fillTip(tip, dot) {
    var title = dot.getAttribute("data-hs-title") || "";
    var body = dot.getAttribute("data-hs-body") || "";
    var img = dot.getAttribute("data-hs-img") || "";
    var imgEl = tip.querySelector(".hotspot-tip-img");
    if (img) { imgEl.src = img; imgEl.hidden = false; }
    else { imgEl.removeAttribute("src"); imgEl.hidden = true; }
    tip.querySelector(".hotspot-tip-title").textContent = title;
    tip.querySelector(".hotspot-tip-body").textContent = body;
  }

  // Position the open tooltip (fixed) relative to the open dot, with edge-flip.
  function position() {
    try {
      if (!openDot || !openTip) return;
      if (!document.contains(openDot)) { close(); return; }
      var d = openDot.getBoundingClientRect();
      var dcx = d.left + d.width / 2, dtop = d.top, dbot = d.bottom;
      var tw = openTip.offsetWidth, th = openTip.offsetHeight;
      var vw = window.innerWidth, gap = 14, pad = 8;
      var below = (dtop - gap - th < pad);
      openTip.classList.toggle("tip-bottom", below);
      var y = below ? (dbot + gap) : (dtop - gap - th);
      var x = dcx - tw / 2;
      if (x < pad) x = pad; else if (x + tw > vw - pad) x = vw - pad - tw;
      var caret = dcx - x; caret = Math.max(14, Math.min(tw - 14, caret));
      openTip.style.setProperty("--caret", caret + "px");
      openTip.style.transform = "translate(" + Math.round(x) + "px," + Math.round(y) + "px)";
    } catch (e) {}
  }

  function track() { if (openDot) { position(); rafId = requestAnimationFrame(track); } }

  function close() {
    if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    if (openDot) { openDot.classList.remove("is-open"); openDot.setAttribute("aria-expanded", "false"); }
    if (openTip) { openTip.classList.remove("is-open"); }
    openDot = null;
  }

  function open(dot) {
    if (openDot === dot) return;
    if (openDot) close();
    var tip = ensureTip();
    fillTip(tip, dot);
    openDot = dot;
    dot.classList.add("is-open");
    dot.setAttribute("aria-expanded", "true");
    tip.classList.add("is-open");
    position();
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(track);
  }

  function dotFrom(target) {
    return target && target.closest ? target.closest(".hotspot-dot") : null;
  }

  function hoverable() {
    return window.matchMedia && window.matchMedia("(hover:hover)").matches;
  }

  // ---- Hover-zoom (magnify.js) awareness ------------------------------------
  // When image_zoom is 'hover', clicking the image activates magnify.js, which
  // inserts a `.image-magnify-full-size` overlay (z-index:1, a panning zoomed
  // background-image crop) ON TOP of the image — and therefore on top of our
  // server-rendered dots. The zoom is NOT a CSS transform on any container the
  // dots live in, so a dot's getBoundingClientRect is unchanged and there is no
  // transform to follow; the dots are simply covered by the magnifier crop.
  // The only sensible behaviour is to suppress the tooltip while the magnifier is
  // active (a tooltip pinned to the un-zoomed dot spot over a zoomed crop is just
  // confusing) and resume normally once magnify removes its overlay.
  function zoomActive() {
    return !!document.querySelector(".image-magnify-full-size");
  }

  // ---- Delegated listeners on document (bound once; survive re-renders) -----
  document.addEventListener("click", function (e) {
    var dot = dotFrom(e.target);
    if (dot) {
      e.preventDefault();
      e.stopPropagation();
      // While the magnifier overlay is up, don't open a tooltip over the zoom.
      if (zoomActive()) { close(); return; }
      (openDot === dot) ? close() : open(dot);
    } else if (openDot) {
      // click anywhere else closes the open tooltip
      close();
    }
  });

  document.addEventListener("mouseover", function (e) {
    if (!hoverable() || zoomActive()) return;
    var dot = dotFrom(e.target);
    if (dot) open(dot);
  });

  document.addEventListener("mouseout", function (e) {
    if (!hoverable() || !openDot) return;
    var dot = dotFrom(e.target);
    // Only close when leaving the currently-open dot for something that isn't it.
    if (dot === openDot) {
      var to = e.relatedTarget;
      if (!to || (to.closest && to.closest(".hotspot-dot") !== openDot)) close();
    }
  });

  document.addEventListener("keydown", function (e) { if (e.key === "Escape") close(); });
  document.addEventListener("scroll", function () { if (openDot) position(); }, true);
  window.addEventListener("resize", function () { if (openDot) position(); });

  // Close the open tooltip when (a) the theme re-renders the gallery and detaches
  // the open dot, or (b) the magnify.js zoom overlay appears over the image. Both
  // are structural churn we only care about while a tooltip is open.
  if (window.MutationObserver) {
    new MutationObserver(function () {
      if (openDot && (!document.contains(openDot) || zoomActive())) close();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
